import { useCallback, useMemo } from "react";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import type { Section, PreviewResponse, PageCountResponse, SectionsResponse } from "@/lib/api";

export interface UsePreviewApiReturn {
  getPageCount: (documentId: string) => Promise<number>;
  getPage: (documentId: string, page: number) => Promise<PreviewResponse>;
  getSections: (documentId: string) => Promise<Section[]>;
  getLatex: (documentId: string) => Promise<PreviewResponse>;
  getFullHtml: (documentId: string) => Promise<PreviewResponse>;
}

/**
 * Hook for preview API calls with proper authentication.
 * Uses fetchWithAuth from ApiContext to include Bearer token.
 */
export function usePreviewApi(): UsePreviewApiReturn {
  const { fetchWithAuth } = useApiContext();

  const getPageCount = useCallback(
    async (documentId: string): Promise<number> => {
      const startTime = performance.now();
      console.debug("[Preview API] Fetching page count for document:", documentId);
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/preview/page-count`
      );
      const fetchTime = performance.now();
      const data = await handleResponse<PageCountResponse>(response);
      const parseTime = performance.now();
      console.debug(`[Preview API] Page count: ${data.count} (fetch: ${(fetchTime - startTime).toFixed(0)}ms, parse: ${(parseTime - fetchTime).toFixed(0)}ms)`);
      return data.count;
    },
    [fetchWithAuth]
  );

  const getPage = useCallback(
    async (documentId: string, page: number): Promise<PreviewResponse> => {
      const startTime = performance.now();
      console.debug("[Preview API] Fetching page", page, "for document:", documentId);
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/preview/html?page=${page}`
      );
      const fetchTime = performance.now();
      const data = await handleResponse<PreviewResponse>(response);
      const parseTime = performance.now();
      console.debug(`[Preview API] Page ${page} loaded (fetch: ${(fetchTime - startTime).toFixed(0)}ms, parse: ${(parseTime - fetchTime).toFixed(0)}ms, size: ${data.content?.length})`);
      return data;
    },
    [fetchWithAuth]
  );

  const getSections = useCallback(
    async (documentId: string): Promise<Section[]> => {
      const startTime = performance.now();
      console.debug("[Preview API] Fetching sections for document:", documentId);
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/preview/sections`
      );
      const fetchTime = performance.now();
      const data = await handleResponse<SectionsResponse>(response);
      const parseTime = performance.now();
      console.debug(`[Preview API] Sections loaded: ${data.sections?.length} (fetch: ${(fetchTime - startTime).toFixed(0)}ms, parse: ${(parseTime - fetchTime).toFixed(0)}ms)`);
      return data.sections;
    },
    [fetchWithAuth]
  );

  const getLatex = useCallback(
    async (documentId: string): Promise<PreviewResponse> => {
      console.debug("[Preview API] Fetching LaTeX for document:", documentId);
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/preview/latex`
      );
      const data = await handleResponse<PreviewResponse>(response);
      console.debug("[Preview API] LaTeX loaded, content length:", data.content?.length);
      return data;
    },
    [fetchWithAuth]
  );

  const getFullHtml = useCallback(
    async (documentId: string): Promise<PreviewResponse> => {
      console.debug("[Preview API] Fetching full HTML for document:", documentId);
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/preview/html/full`
      );
      const data = await handleResponse<PreviewResponse>(response);
      console.debug("[Preview API] Full HTML loaded, content length:", data.content?.length);
      return data;
    },
    [fetchWithAuth]
  );

  // Memoize the return object to prevent infinite re-renders
  // when used as a useEffect dependency
  return useMemo(
    () => ({
      getPageCount,
      getPage,
      getSections,
      getLatex,
      getFullHtml,
    }),
    [getPageCount, getPage, getSections, getLatex, getFullHtml]
  );
}
